import { getAccessToken } from "config/token";
import { NEXUS_API_BASE_URL, NEXUS_API_BASE_URL_V2 } from "const";
import { useMsal, IMsalContext } from "@azure/msal-react";
import axios, { AxiosResponse } from "axios";

import { $bucketUrlHost, $datazoneUrlHost } from "../../http/index";
import { Credentials } from "../../components/Users/components/AccessData/types";
import { getHeaders } from "../../helpers";
import { S3Client, ListObjectsV2Command, ListBucketsCommand, ListObjectsCommand, ListObjectsCommandOutput } from "@aws-sdk/client-s3";
import { IDomainsConfig } from "pages/Ponds/types";
import {
  IPublicClientApplication,
  PublicClientApplication,
} from "@azure/msal-browser";
import { RawSpace } from "utils/normalizeSpaces";
import { FetchSpacesAdvancedParams, FetchSpacesAdvancedResponse } from "./types";
// import { msalConfig } from "./config/auth";

// credential names for storage:
const tokenKeys = {
  s3AwsKeyId: "S3-AWS-Key-Id",
  s3AwsSecretKey: "S3-AWS-Secret-Key",
  s3AwsCredsType: "S3-AWS-Creds-Type",
  s3AwsExpirationTimestamp: "S3-AWS-Expiration-Timestamp",
  msalAccountKeys: "msal.account.keys",
};


export const fetchDomainsConfig = async (msalInstance: IMsalContext): Promise<IDomainsConfig[]> => {
  const token = await getAccessToken(msalInstance);

  const bearerToken = "Bearer " + (token == undefined ? "" : token.token);
  const requestURL: string = NEXUS_API_BASE_URL + "/nexus/get-domains";
  const config = { headers: { "Authorization": bearerToken } };

  try {
    const res = await axios.get<IDomainsConfig[]>(requestURL, config);
    return res.data.map(item => item);
  } catch (error) {
    console.error("Error fetching Domains Config:", error);
    return [];
  }
};

export const fetchListBucketsAdvanced = async (
  params: FetchSpacesAdvancedParams,
): Promise<FetchSpacesAdvancedResponse> => {
  const {
    msalInstance,
    region,
    domain,
    scope = "all",
    searchSpaceName,
    projectTypeFilter,
    profileTypeFilter,
    bucketTypeFilter,
    classificationFilter,
    isActiveFilter,
    groupFilter,
    licensingFilter,
    studyIdFilter,
    projectIdFilter,
    sortFields,
    ownerFilter,
    page,
    pageSize,
    limit,
  } = params;

  // Build query parameters adding pagination_needed=true
  const queryParams = new URLSearchParams();

  queryParams.append("pagination_needed", "true");

  // Required parameters
  queryParams.append("scope", scope);
  queryParams.append("region_name", region);
  queryParams.append("domain_name", domain);

  // Optional parameters
  if (searchSpaceName) {
    queryParams.append("search_space_name", searchSpaceName);
  }

  if (projectTypeFilter && projectTypeFilter.length > 0) {
    queryParams.append("project_type_filter", projectTypeFilter.join(","));
  }

  if (profileTypeFilter && profileTypeFilter.length > 0) {
    queryParams.append("profile_type_filter", profileTypeFilter.join(","));
  }

  if (ownerFilter && ownerFilter.length > 0) {
    queryParams.append("owner_filter", ownerFilter.join(","));
  }

  if (classificationFilter && classificationFilter.length > 0) {
    queryParams.append("classification_filter", classificationFilter.join(","));
  }

  if (isActiveFilter && isActiveFilter.length > 0) {
    queryParams.append("is_active_filter", isActiveFilter.join(","));
  }

  if (groupFilter && groupFilter.length > 0) {
    queryParams.append("group_filter", groupFilter.join(","));
  }

  if (licensingFilter && licensingFilter.length > 0) {
    queryParams.append("licensing_obligation_filter", licensingFilter);
  }

  if (studyIdFilter && studyIdFilter.length > 0) {
    queryParams.append("study_id_filter", studyIdFilter.join(","));
  }

  if (projectIdFilter && projectIdFilter.length > 0) {
    queryParams.append("project_id_filter", projectIdFilter.join(","));
  }

  if (bucketTypeFilter && bucketTypeFilter.length > 0) {
    queryParams.append("bucket_type_filter", bucketTypeFilter.join(","));
  }

  if (sortFields && sortFields.length > 0) {
    const sortFieldsJson = JSON.stringify(sortFields);
    queryParams.append("sort_fields", sortFieldsJson);
  }

  if (page !== undefined) {
    queryParams.append("page", page.toString());
  }

  if (pageSize !== undefined) {
    queryParams.append("page_size", pageSize.toString());
  }

  if (limit !== undefined) {
    queryParams.append("limit", limit.toString());
  }

  const requestURL = `${NEXUS_API_BASE_URL_V2}/nexus/spaces?${queryParams.toString()}`;

  const token = await getAccessToken(msalInstance);
  const bearerToken = "Bearer " + (token == undefined ? "" : token.token);
  const config = {
    headers: {
      "Authorization": bearerToken,
      // Only send the Graph token for scope=accessible — the backend needs it
      // to check AD group membership. Not required for "all" or "owned" scopes.
      ...(scope === "accessible" && token?.at ? { "X-Graph-Token": token.at } : {}),
    },
  };

  try {
    const res = await axios.get(requestURL, config);

    // Handle different response formats
    let spaces: RawSpace[] = [];
    let pagination = undefined;

    if (res.data.spaces && Array.isArray(res.data.spaces)) {
      // New format with pagination
      spaces = res.data.spaces as RawSpace[];
      pagination = res.data.pagination;
    } else if (Array.isArray(res.data)) {
      // Legacy format - direct array
      spaces = res.data as RawSpace[];
    } else if (Array.isArray(res.data.buckets)) {
      // Legacy format - buckets array
      spaces = res.data.buckets as RawSpace[];
    }

    return {
      spaces,
      pagination,
    };

  } catch (error) {
    console.error("Error fetching spaces list:", error);
    return {
      spaces: [],
      pagination: undefined,
    };
  }
};

const extractPrefixesFromS3Xml = (commonPrefixes: Array<{ Prefix?: string }> = []): string[] => {
  return commonPrefixes.map((prefixObj) => prefixObj.Prefix ?? "");
};
const fetchSpaces = async (
  msalInstance: IMsalContext,
  bucket_name: string,
  region_name: string,
): Promise<{
  data: any;
}> => {
  try {
    const token = await getAccessToken(msalInstance);
    const _headers = getHeaders(token?.token);
    const headers = {
      "Authorization": _headers.Authorization,
      "Content-Type": _headers["Content-Type"],
    };
    const data: AxiosResponse<{ data: { session: Credentials } }> =
      await $datazoneUrlHost.get(`v2/s3buckets/${bucket_name}/keys`, {
        headers: headers,
      });
    const client = new S3Client({

      logger: console, // Enable AWS SDK logging
      region: region_name,
      credentials: {
        accessKeyId: data.data.data.session.AccessKeyId,
        secretAccessKey: data.data.data.session.SecretAccessKey,
      },
    });
    const input = {
      "Bucket": bucket_name,
      "Prefix": "",
      "Delimiter": "/",
    };
    const command = new ListObjectsCommand(input);
    // const input = { // ListBucketsRequest
    //   Prefix: "*",
    //   BucketRegion: region_name,
    // };
    // const command = new ListBucketsCommand(input);
    const response: ListObjectsCommandOutput = await client.send(command);



    const resData = extractPrefixesFromS3Xml(response.CommonPrefixes ?? []);
    return {
      data: resData,
    };
  } catch (error) {
    return {
      data: [],
    };
  }
};

export const getSpacesByDomainName = async (
  msalInstance: IMsalContext,
  buckets: string[],
  region = "us-east-1") => {
  try {
    const outputArray = [];
    for (const bucket of buckets) {
      const response = await fetchSpaces(msalInstance, bucket, region);
      return response;
    }
  } catch (error) {
    return {
      data: [],
    };
  }
};

const saveDataToStorage = async (data = {}) => {
  return new Promise((resolve) => {
    Object.entries(data).forEach(([key, value]) => {
      localStorage.setItem(key, String(value));
    });

  });
};

export const getAwsKeys = async (msalInstance: any) => {
  const REFETCH_COUNT = 2;
  const REFETCH_TIMEOUT = 2000;
  const bucketName = "";
  const awsKeysUrl = "https://nexus-dev.ph-dse.int.bayer.com/v2/s3buckets/bay-ph-rnd-dse-nexus-analytical-data-dev/keys";

  const token = await getAccessToken(msalInstance);
  const bearerToken = "Bearer " + (token == undefined ? "" : token.token);

  const authToken = bearerToken;
  let attempt = 0;

  while (attempt <= REFETCH_COUNT) {
    try {
      const response = await fetch(awsKeysUrl, {
        method: "GET",
        headers: {
          Authorization: authToken,
        },
      });

      type AwsSessionResponse = {
        data: {
          session: {
            AccessKeyId: string;
            SecretAccessKey: string;
            ExpirationTimestamp: string;

          };
        };
      };
      const data: AwsSessionResponse = await response.json();

      const { AccessKeyId, SecretAccessKey, ExpirationTimestamp } =
        data?.data?.session || {};

      const awsKeys = {
        [tokenKeys.s3AwsKeyId]: AccessKeyId,
        [tokenKeys.s3AwsSecretKey]: SecretAccessKey,
        [tokenKeys.s3AwsCredsType]: "user",
        [tokenKeys.s3AwsExpirationTimestamp]: ExpirationTimestamp,
      };

      await saveDataToStorage(awsKeys);

      return data; // resolve equivalent
    } catch (error) {
      console.error("Get credentials error:", error);
      if (attempt === REFETCH_COUNT) {
        throw error; // reject equivalent
      }
      await new Promise((res) => setTimeout(res, REFETCH_TIMEOUT));
      attempt++;
    }
  }
};